import { useEffect, useMemo, useRef, useState } from "react";
import SettingStore from "stores/settings";
import * as CONSTANTS from "../../constants";
import {
  applyMyLearningFiltersToSearchParams,
  parseHashPathAndQuery,
  parseMyLearningFilterParamsFromHash,
  replaceHashPreservingPath,
} from "./myLearningFilterUrl";
import {
  applyLegacyMycoursesFilterTagsFromHash,
  buildPaddedSelectedTags,
  resolveCategoryIdForHydration,
} from "./myLearningFilterHydration.utils";

export const useMyLearningFilterUrlSync = ({
  mode,
  courses,
  programs,
  searching,
  tags,
  categories,
  contextFilter,
  contextFilterRef,
  fetchMyLearningData,
  applyFilter,
  updateState,
  setTagFilter,
}) => {
  const [urlSyncReady, setUrlSyncReady] = useState(false);
  const hydrationLockRef = useRef(false);

  const validStatusValues = useMemo(
    () => CONSTANTS.SORT_OPTIONS_STATUS.map((o) => o.value),
    [],
  );
  const validSortValues = useMemo(
    () => CONSTANTS.SORT_OPTIONS_ORDER.map((o) => o.value),
    [],
  );

  const filterForUrl = useMemo(() => {
    if (!contextFilter) {
      return {};
    }
    return {
      searchText: contextFilter.searchText,
      selectedTags: contextFilter.selectedTags,
      categoryId: contextFilter.categoryId,
      status: contextFilter.status,
      sortBy: contextFilter.sortBy,
    };
  }, [
    contextFilter?.searchText,
    contextFilter?.selectedTags,
    contextFilter?.categoryId,
    contextFilter?.status,
    contextFilter?.sortBy,
  ]);

  useEffect(() => {
    if (hydrationLockRef.current) {
      return;
    }
    const dataReady =
      (courses?.length > 0 || programs?.length > 0) && !searching;
    if (!dataReady) {
      return;
    }

    const parsed = parseMyLearningFilterParamsFromHash(
      validStatusValues,
      validSortValues,
    );

    if (
      parsed.selectedTags &&
      parsed.selectedTags.some((t) => t !== 0) &&
      (!tags || tags.length === 0)
    ) {
      return;
    }

    hydrationLockRef.current = true;

    const latestFilter = contextFilterRef.current;

    if (parsed.hasMyLearningParams) {
      const selectedTags = buildPaddedSelectedTags(
        parsed.selectedTags,
        tags,
        latestFilter,
      );

      const categoryId = resolveCategoryIdForHydration(
        parsed.categoryId,
        categories,
      );

      const searchText =
        parsed.searchText != null
          ? String(parsed.searchText)
          : latestFilter.searchText;
      const status = parsed.status || latestFilter.status;
      const sortBy = parsed.sortBy || latestFilter.sortBy;

      if (parsed.status) {
        localStorage.setItem(
          CONSTANTS.STORAGE_KEY_FILTER_STATUS,
          parsed.status,
        );
      }
      if (parsed.sortBy) {
        localStorage.setItem(CONSTANTS.STORAGE_KEY_SORT_ORDER, parsed.sortBy);
      }

      const needsServerFetch =
        selectedTags.some((t) => t !== 0) ||
        (searchText && searchText.trim().length >= 2);

      const filterPayload = {
        searchText,
        selectedTags,
        sortBy,
        mode,
        filter: latestFilter.filter,
        categoryId,
        displayAdminCourses: latestFilter.displayAdminCourses,
        status,
      };

      if (needsServerFetch) {
        fetchMyLearningData(filterPayload, true);
      } else {
        applyFilter({ ...latestFilter, ...filterPayload });
      }
    } else {
      const tagString = SettingStore.getSettingValue("mycoursesfiltertags");
      if (tagString && tags?.length) {
        const tagsList = tagString.split(",");
        const hashQuery = window.location.hash.split("?")[1];
        const queryParams = new URLSearchParams(hashQuery || "");

        applyLegacyMycoursesFilterTagsFromHash({
          tagsList,
          tags,
          categories,
          queryParams,
          updateState,
          setTagFilter,
        });
      }
    }

    setUrlSyncReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot hydration when data becomes ready
  }, [
    searching,
    courses,
    programs,
    tags,
    categories,
    validStatusValues,
    validSortValues,
  ]);

  useEffect(() => {
    if (!urlSyncReady) {
      return;
    }
    const { params } = parseHashPathAndQuery();
    const defaultSortBy =
      localStorage.getItem(CONSTANTS.STORAGE_KEY_SORT_ORDER) ||
      CONSTANTS.SORT_CREATED_DATE;
    applyMyLearningFiltersToSearchParams(
      params,
      filterForUrl,
      Math.max(
        tags?.length || 0,
        filterForUrl.selectedTags?.length ||
          CONSTANTS.FILTER_INITIAL_TAGS.length,
      ),
      {
        defaultStatus: CONSTANTS.PROGRESS_ALL,
        defaultSortBy,
      },
    );
    replaceHashPreservingPath(params);
  }, [urlSyncReady, filterForUrl, tags?.length]);
};
